/**
 * Block 12 PR-D-A Phase D5 Step 10 — feature-local wrapper над existing
 * `pomoshnik-offline-v1.quotes` store.
 *
 * **Дизайн:** existing `lib/offline/idb/quotes.ts` остаётся generic
 * (data: unknown — storage layer agnostic). Этот модуль добавляет
 * domain-typed surface: `SavedQuote` (с typed QuoteState data + name)
 * и `saveQuoteWithState` / `loadSavedQuote` / `listSavedQuotes` /
 * `deleteSavedQuote`. Compiler-narrowed type-safety без изменения
 * lib-layer interface (избегаем circular dep features → lib).
 *
 * **Reuse existing store** (decision Aibek 2026-05-18): НЕ создаём
 * новую DB `saved-quotes`. Используем `quotes` store из
 * `pomoshnik-offline-v1` (Phase A1 schema). QuoteRecord.data: unknown
 * хранит QuoteState + `name` field stored at top level.
 *
 * **Schema mapping QuoteRecord ↔ SavedQuote:**
 *   - QuoteRecord.id ↔ SavedQuote.id (UUID)
 *   - QuoteRecord.projectId ↔ SavedQuote.draftKey (projectId либо 'standalone')
 *   - QuoteRecord.data ↔ SavedQuote.state (QuoteState, narrowed via type cast)
 *   - QuoteRecord.pdfBlobKey: null (PR-D-B заполнит при PDF upload)
 *   - QuoteRecord.syncStatus: 'local' (PR-D-B sync queue promote'нет)
 *   - QuoteRecord.createdAt / updatedAt — Unix ms timestamps
 *   - QuoteRecord — NEW field `name` added через type augmentation
 *     (см. ниже — extra field beyond schema is allowed by IDB structured
 *     clone, и type cast делает его first-class в feature API).
 *
 * BACKLOG B-114 (backend counter sync), B-115 (PR-D-B R2 + signed URL).
 */

import type { QuoteRecord } from '@/lib/offline/idb/database';
import {
  saveQuote as saveQuoteRaw,
  getQuote as getQuoteRaw,
  deleteQuote as deleteQuoteRaw,
  listAllQuotes as listAllQuotesRaw,
} from '@/lib/offline/idb/quotes';
import type { QuoteState } from '../types';

/**
 * Domain-typed запись сохранённого КП. Hydration restore через
 * `state` поле; `name` для list-view («Кухня №1») в Block 11 ЛК.
 */
export interface SavedQuote {
  id: string;
  /** projectId анкеты-источника либо 'standalone'. */
  draftKey: string;
  /** Auto-counter-generated «{Тип мебели} №{N}» (Phase D5 Step 11). */
  name: string;
  /** Snapshot quote state на момент save. */
  state: QuoteState;
  createdAt: number;
  updatedAt: number;
}

/**
 * Расширенный QuoteRecord — type cast в storage. IDB structured clone
 * сохраняет любые JSON-safe поля без schema enforcement, поэтому
 * добавление `name` поверх существующего interface — runtime-safe.
 */
type SavedQuoteRecord = QuoteRecord & { name: string };

function toRecord(saved: SavedQuote): SavedQuoteRecord {
  return {
    id: saved.id,
    projectId: saved.draftKey,
    data: saved.state,
    name: saved.name,
    pdfBlobKey: null,
    syncStatus: 'local',
    createdAt: saved.createdAt,
    updatedAt: saved.updatedAt,
  };
}

function fromRecord(record: QuoteRecord): SavedQuote {
  const named = record as SavedQuoteRecord;
  return {
    id: named.id,
    draftKey: named.projectId,
    name: named.name ?? '(без названия)',
    state: named.data as QuoteState,
    createdAt: named.createdAt,
    updatedAt: named.updatedAt,
  };
}

export async function saveQuoteWithState(saved: SavedQuote): Promise<void> {
  await saveQuoteRaw(toRecord(saved));
}

export async function loadSavedQuote(id: string): Promise<SavedQuote | undefined> {
  const record = await getQuoteRaw(id);
  if (!record) return undefined;
  return fromRecord(record);
}

export async function listSavedQuotes(): Promise<SavedQuote[]> {
  const records = await listAllQuotesRaw();
  return records.map(fromRecord);
}

export async function deleteSavedQuote(id: string): Promise<void> {
  await deleteQuoteRaw(id);
}

/**
 * PR-H1 #2 fix — найти latest savedQuote (by updatedAt desc) с matching draftKey.
 *
 * Используется QuotePageContainer hydration cascade Priority 2.5: после
 * `handleSave` намеренно clear'ит draft IDB + Zustand (mirror C5 pattern,
 * line 147-148 BottomActionButtons.tsx), browser back к `/calculator/quote?
 * projectId=<X>` не находит ничего в drafts и сваливался в seed/blank, теряя
 * только что сохранённый snapshot. Эта функция позволяет cascade использовать
 * savedQuote как canonical post-save state.
 *
 * Возвращает `undefined` если нет ни одного savedQuote с этим draftKey.
 * Если несколько — берёт с максимальным `updatedAt` (последний save wins).
 *
 * Linear scan через `listSavedQuotes()` — для текущего масштаба (single user,
 * количество saved quotes в десятках, не тысячах) приемлемо. Если в future
 * количество вырастет — добавить IDB index по draftKey (см. B-114).
 */
export async function loadLatestSavedQuoteByDraftKey(
  draftKey: string,
): Promise<SavedQuote | undefined> {
  const all = await listSavedQuotes();
  const matching = all.filter((q) => q.draftKey === draftKey);
  if (matching.length === 0) return undefined;
  return matching.reduce((latest, q) => (q.updatedAt > latest.updatedAt ? q : latest));
}
